import Checkbox from 'antd/lib/checkbox/Checkbox';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useSession } from 'next-auth/react';
import { useEffect, useState } from 'react';
import { useIntercom } from 'react-use-intercom';

import theme from '../assets/theme';
import { Loader } from '../components/_atoms/_Loader';
import { Button } from '../components/_atoms/Button';
import { NextPageWithLayout } from '../components/layouts';
import useAccessBadge from '../hooks/useAccessBadge';
import useExport from '../hooks/useExport';
import useRouterMatch from '../hooks/useRouterMatch';
import useSignIn from '../hooks/useSignIn';
import useSignOut from '../hooks/useSignOut';
import { CenteredContainer } from '../styles/CenteredContainer';
import { detectAndBestEffortSetLanguage } from '../utils/localisationUtils';

const UnauthenticatedExperience = () => {
  const { signIn } = useSignIn();

  useEffect(() => {
    signIn();
  }, [signIn]);

  return (
    <CenteredContainer style={{ height: '100%' }}>
      <Loader />
    </CenteredContainer>
  );
};

const AuthenticatedExperience = () => {
  const {
    checkToken,
    isCheckingToken,
    checkTokenResponse,
    checkTokenSuccess,
    executeExport,
    isCheckTokenError,
    executeExportSuccess,
    isExecutingExport,
    executeExportError,
  } = useExport();
  const { tenantId: activeTenantId } = useRouterMatch();

  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const router = useRouter();

  const token = router.query.token as string;

  const { accessBadge: userAccessBadge } = useAccessBadge();

  const { b2cSignout } = useSignOut();
  const { show } = useIntercom();

  useEffect(() => {
    if (activeTenantId && token) {
      // Upon first client render, we check if the token is valid
      checkToken({ token, activeTenantId });
    }
  }, [checkToken, token, activeTenantId]);

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      // Logout to the unauthenticated page to help the user login again.
      b2cSignout(window.location.href);
    }
  };

  return (
    <div style={{ height: '100%', width: '100%', padding: 40 }}>
      <div
        style={{
          height: '100%',
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          flexDirection: 'column',
          padding: 40,
          background: 'white',
          borderRadius: 10,
          boxShadow: theme.effects.floatingCreatorElementShadow,
        }}
      >
        <>
          <h1>Import content</h1>

          {isCheckingToken && <Loader />}

          {isCheckTokenError && (
            <>
              <p>
                The link will not work on this account. Please login with a different
                account.
              </p>
              <Button type="primary" onClick={() => handleLogout()}>
                Change account
              </Button>
            </>
          )}
          {!executeExportSuccess && checkTokenSuccess && checkTokenResponse && token && (
            <>
              {
                /* We have a verified token that can be executed.  */
                <div
                  style={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    flexDirection: 'column',
                    padding: 40,
                  }}
                >
                  <h3>You are now importing lessons and reports from</h3>
                  <span style={{ fontWeight: 'bold', fontSize: '26px' }}>
                    {checkTokenResponse.payload.issuedBy}{' '}
                    <span style={{ color: theme.colors.greyDark }}>
                      ({checkTokenResponse.tenantName})
                    </span>
                  </span>
                  <span>into</span>
                  <span style={{ fontWeight: 'bold', fontSize: '26px' }}>
                    {userAccessBadge?.user.email}{' '}
                    <span style={{ color: theme.colors.greyDark }}>
                      ({userAccessBadge?.organization?.name})
                    </span>
                  </span>
                  <span
                    style={{
                      textDecoration: 'underline',
                      fontSize: '14px',
                      cursor: 'pointer',
                    }}
                    onClick={() => handleLogout()}
                  >
                    change account
                  </span>
                  <div style={{ maxWidth: 280, marginTop: 40, display: 'flex' }}>
                    <Checkbox
                      checked={isConfirmed}
                      onChange={val => setIsConfirmed(val.target.checked)}
                    />
                    <span style={{ paddingLeft: 10 }}>
                      {`I confirm that all the private lessons and reports from
                  ${checkTokenResponse.payload.issuedBy} will be copied to ${userAccessBadge?.user.email}'s
                  account`}
                    </span>
                  </div>
                  <Button
                    type="primary"
                    styles={{ marginTop: 20, width: 280 }}
                    loading={isExecutingExport}
                    disabled={!isConfirmed || !!executeExportError || isExecutingExport}
                    onClick={() => executeExport({ token, activeTenantId })}
                  >
                    Copy content
                  </Button>
                </div>
              }
            </>
          )}

          {executeExportSuccess && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <p>Successfully moved all lessons and report to your account!</p>
              <Button
                type="primary"
                onClick={() => router.push(`/${activeTenantId}/lessons`)}
              >
                Go to my lessons
              </Button>
            </div>
          )}
          {executeExportError && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexDirection: 'column',
              }}
            >
              <p>
                An error occured while copying your content. Please reach out to customer
                support to get help.
              </p>
              <Button type="primary" onClick={() => show()}>
                Get help
              </Button>
            </div>
          )}
        </>
      </div>
    </div>
  );
};

const Import: NextPageWithLayout = () => {
  const session = useSession();

  if (session.status === 'authenticated') {
    return <AuthenticatedExperience />;
  }

  if (session.status === 'unauthenticated') {
    return <UnauthenticatedExperience />;
  }

  return (
    <CenteredContainer style={{ height: '100%' }}>
      <Loader />
    </CenteredContainer>
  );
};
export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=300, stale-while-revalidate=3600',
  );
  return {
    props: {
      ...(await detectAndBestEffortSetLanguage(context)),
    },
  };
};
export default Import;
